import { Injectable } from "@angular/core";
import { Observable, delayWhen, first, from, map, mergeMap, take } from "rxjs";
import { BroadcastService } from "@webapp/core/broadcast/services/broadcast.service";
import { SubNavOption } from "@webapp/navigation/models/sub-nav-options.models";
import { SettingsVisibilityService } from "./settings-visibility.service";

@Injectable()
export class SettingsSubNavStateService {
  public showSystemOptions$ = this.settingsVisibility.systemOptions.anyOption$;
  public showUserOptions$ = this.settingsVisibility.userOptions.anyOption$;
  public showIntegrationOptions$ = this.settingsVisibility.integrationsOptions.anyOption$;
  public showAccountOptions$ = this.settingsVisibility.accountOptions.anyOption$;

  public systemOptions: SubNavOption[] = [
    { label: "Methodology", icon: "map", state: "gtmhub.configuration.methodology", showOptionCondition$: this.settingsVisibility.systemOptions.methodology$ },
    {
      label: "Tag Management",
      icon: "tag",
      state: "gtmhub.configuration.tags",
      showOptionCondition$: this.settingsVisibility.systemOptions.tagManagement$,
    },
    {
      label: "Language",
      icon: "public",
      state: "gtmhub.configuration.language",
      showOptionCondition$: this.settingsVisibility.systemOptions.language$,
    },
    {
      label: "Reports and Notifications",
      icon: "notifications",
      state: "gtmhub.configuration.reportsAndNotifications",
      showOptionCondition$: this.settingsVisibility.systemOptions.reportsAndNotifications$,
    },
    {
      label: "Features",
      icon: "wizard",
      state: "gtmhub.configuration.features",
      showOptionCondition$: this.settingsVisibility.systemOptions.features$,
    },
    {
      label: "Custom Fields",
      icon: "drag-drop",
      state: "gtmhub.configuration.customFields",
      showOptionCondition$: this.settingsVisibility.systemOptions.customFields$,
    },
    {
      label: "Insightboard Parameters",
      icon: "custom-field",
      state: "gtmhub.configuration.parameters",
      showOptionCondition$: this.settingsVisibility.systemOptions.insightboardParameters$,
    },
    {
      label: "Automation",
      icon: "automation",
      state: "gtmhub.automationList",
      showOptionCondition$: this.settingsVisibility.systemOptions.automation$,
    },
  ];
  public usersOptions: SubNavOption[] = [
    { label: "Users", icon: "users-group", state: "gtmhub.usersList", showOptionCondition$: this.settingsVisibility.userOptions.users$ },
    { label: "Manage Roles", icon: "live-demo", state: "gtmhub.roles", showOptionCondition$: this.settingsVisibility.userOptions.manageRoles$ },
  ];
  public integrationsOptions: SubNavOption[] = [
    { label: "Data Sources", icon: "sql", state: "gtmhub.dataflow", showOptionCondition$: this.settingsVisibility.integrationsOptions.dataSources$ },
    {
      label: "Installed Apps",
      icon: "integrations",
      state: "gtmhub.configuration.pluginList",
      showOptionCondition$: this.settingsVisibility.integrationsOptions.installedApps$,
    },
    { label: "API Tokens", icon: "key", state: "gtmhub.configuration.apiTokens", showOptionCondition$: this.settingsVisibility.integrationsOptions.apiTokens$ },
    { label: "Slack", icon: "slack", state: "gtmhub.configuration.slack", showOptionCondition$: this.settingsVisibility.integrationsOptions.slack$ },
    { label: "Jira", icon: "jira", state: "gtmhub.configuration.jira", showOptionCondition$: this.settingsVisibility.integrationsOptions.jira$ },
    {
      label: "Workday",
      icon: "workday",
      state: "gtmhub.configuration.workday",
      showOptionCondition$: this.settingsVisibility.integrationsOptions.workday$,
    },
  ];
  public accountOptions: SubNavOption[] = [
    {
      label: "Account and Domain",
      icon: "public",
      state: "gtmhub.configuration.accountNameDomainChange",
      showOptionCondition$: this.settingsVisibility.accountOptions.accountAndDomain$,
    },
    { label: "Branding", icon: "branding", state: "gtmhub.configuration.branding", showOptionCondition$: this.settingsVisibility.accountOptions.branding$ },
    {
      label: "Billing",
      icon: "credit-card",
      state: "gtmhub.configuration.billing",
      showOptionCondition$: this.settingsVisibility.accountOptions.billing$,
    },
    {
      label: "Single Sign On",
      icon: "security",
      state: "gtmhub.configuration.singleSignOn",
      showOptionCondition$: this.settingsVisibility.accountOptions.singleSignOn$,
    },
    {
      label: "Audit log",
      icon: "audit-log",
      state: "gtmhub.configuration.auditLog",
      showOptionCondition$: this.settingsVisibility.accountOptions.auditLog$,
    },
  ];

  constructor(private broadcastService: BroadcastService, private settingsVisibility: SettingsVisibilityService) {
    this.usersOptions.splice(1, 0, this.getBadges());

    this.broadcastService.on("FeatureFlagUpdated").subscribe(() => {
      this.showUserOptions$ = this.settingsVisibility.userOptions.anyOption$;

      this.usersOptions.splice(1, 1, this.getBadges());
    });
  }

  public firstVisibleState$(): Observable<string> {
    const settingsOptions: Observable<SubNavOption> = from([...this.systemOptions, ...this.usersOptions, ...this.integrationsOptions, ...this.accountOptions]);

    return settingsOptions.pipe(
      delayWhen(() => this.settingsVisibility.showSettings$),
      map((s) =>
        s.showOptionCondition$.pipe(
          take(1),
          map((v) => {
            if (v) {
              return s;
            } else {
              return null;
            }
          })
        )
      ),
      mergeMap((v) => v),
      first((v) => !!v),
      map((s) => s.state)
    );
  }

  private getBadges(): SubNavOption {
    return {
      label: "Badges",
      icon: "award",
      state: "gtmhub.badges",
      showOptionCondition$: this.settingsVisibility.userOptions.badges$,
    };
  }
}
